import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Checkbox, Form, Select } from "semantic-ui-react";
import PropTypes from "prop-types";
import { permissions } from "../../constant/permissions";
import { EditEntity } from "../../components/common/edit-entity";
import {
  clearError,
  createEstate,
  deleteEstate,
  updateEstate,
} from "../../redux/modules/estates";
import {
  FormdataManager,
  getInitialState,
} from "../../components/common/formdata";
import { ErrorWrap } from "../../helpers/error";
import { t } from "../../constant/translations";
import { useHistory } from "react-router";
import { reverse, routes } from "../../routes";

export const EstateEditModalComponent = ({
  entity,
  initialData,
  buttonContent,
}) => {
  const [formData, setFormData] = useState(null);

  const loading = useSelector((state) => state.estates.pending);
  const error = useSelector((state) =>
    ErrorWrap.fromError(state.estates.error)
  );
  const lastModified = useSelector((state) => state.estates.lastModified);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const history = useHistory();

  const buildTypeOptions = () => {
    const availableTypes = [
      "apartment",
      "garage",
      "office",
      "store",
      "parking_space",
      "studio",
      "shop",
      "atelier",
      "restaurant",
      "gym",
      "laboratory",
      "other",
    ];
    return availableTypes.map((entry) => {
      return {
        key: entry,
        value: entry,
        text: t(`estateTypes.${[entry]}`) || entry,
      };
    });
  };

  const handleCreate = (data) => {
    return dispatch(createEstate(data)).then((success) => {
      const estate = success.result;
      history.push(
        reverse(routes.estateDetails, {
          blockId: estate.block.id,
          estateId: estate.id,
        })
      );
    });
  };

  const handleDelete = (data) => {
    dispatch(deleteEstate(data)).then(() => {
      history.push(
        reverse(routes.blockEstateList, {
          blockId: data.block.id,
        })
      );
    });
  };

  const handleUpdate = (data) => dispatch(updateEstate(data));
  const handleClearError = () => dispatch(clearError());

  const initialState = getInitialState(entity, initialData);
  const manager = new FormdataManager(
    formData || initialState,
    setFormData,
    initialState,
    handleClearError
  ).withError(error);

  const canDeleteEstate = user && user.hasPermission(permissions.estateDelete);

  return (
    <EditEntity
      manager={manager}
      entity={entity}
      loading={loading}
      error={error}
      lastModified={lastModified}
      create={handleCreate}
      update={handleUpdate}
      del={canDeleteEstate ? handleDelete : undefined}
      clearError={handleClearError}
      header={!entity ? t("estate.add_estate") : t("estate.change_estate")}
      trigger_props={{
        content: buttonContent,
      }}
    >
      <Form.Field>
        <label>{t("labels.name")}</label>
        <Form.Input
          placeholder={t("labels.name")}
          {...manager.getAttributes("name")}
        />
      </Form.Field>
      <Form.Field>
        <label>{t("labels.type")}</label>
        <Select
          placeholder={t("estate.select_type")}
          options={buildTypeOptions()}
          {...manager.getAttributes("etype")}
        />
      </Form.Field>
      <Form.Field>
        <label>{t("estate.floor")}</label>
        <Form.Input
          type="number"
          placeholder={t("estate.floor")}
          {...manager.getAttributes("floor")}
        />
      </Form.Field>
      <Form.Field>
        <label>{t("estate.preferred_payment_method")}</label>
        <Select
          placeholder={t("estate.pick_payment_method")}
          options={[
            { key: "cash", value: "cash", text: t("billing.cash") },
            { key: "bank", value: "bank", text: t("billing.bank") },
            {
              key: "easypay",
              value: "easypay",
              text: t("billing.easypay"),
            },
            { key: "epay", value: "epay", text: t("billing.epay") },
          ]}
          {...manager.getAttributes("preferred_payment_method")}
        />
        {error.has("preferred_payment_method") && (
          <span>{error.get("preferred_payment_method")}</span>
        )}
      </Form.Field>
      <Form.Field>
        <label>{t("estate.ownership_share_pct")}</label>
        <Form.Input
          type="number"
          step="0.00001"
          min="0"
          max="100"
          placeholder="0.00000"
          {...manager.getAttributes("ownership_share_pct")}
        />
      </Form.Field>
      <Form.Field>
        <Checkbox
          toggle
          label={t("estate.automatic_tax")}
          checked={Boolean(manager.getValue("charging_enabled"))}
          onClick={() => {
            manager.setValue(
              "charging_enabled",
              !manager.getValue("charging_enabled")
            );
          }}
        />
      </Form.Field>
    </EditEntity>
  );
};

EstateEditModalComponent.propTypes = {
  entity: PropTypes.object,
  initialData: PropTypes.object,
  buttonContent: PropTypes.string,
};

export const EstateEditModal = EstateEditModalComponent;
